
import { useIntl } from 'react-intl'
import styled from '@emotion/styled'
import { ButtonGroup, Button, Box, Chip } from '@mui/material'
import { courseSalesApi } from '../../../features/sales/courseSalesSlice'
import { H3 } from '../../../layouts/TextStyles'
import { useEffect, useState } from 'react'
import { CourseSubjectState, CourseWithPricingState } from '../../../features/types'
import CourseCard from './components/CourseCard'
import { ui } from '../../../utils/uiTokens'


const SellCoursePage = () => {
    const { formatMessage } = useIntl()
    const [selectedSubjects, setSelectedSubjects] = useState<number[]>([4])
    const [videoCourse, setVideoCourse] = useState<CourseWithPricingState | null>(null)
    const [monthlyCourse, setMonthlyCourse] = useState<CourseWithPricingState | null>(null)
    const [yearlyCourse, setYearlyCourse] = useState<CourseWithPricingState | null>(null)
    const [paymentType, setPaymentType] = useState('yearly');

    const COMPATIBLE = [
        { subjectId: 8, pairs: [4, 7, 9] },        // Биология
        { subjectId: 7, pairs: [4, 8, 6] },        // Химия
        { subjectId: 5, pairs: [4, 6, 17, 9] },    // Математика
        { subjectId: 6, pairs: [4, 5, 7] },        // Физика
        { subjectId: 17, pairs: [4, 5] },          // Информатика
        { subjectId: 9, pairs: [4, 8, 5, 10, 13] },// География
        { subjectId: 10, pairs: [4, 13, 16, 9] },  // Всемирная история
        { subjectId: 13, pairs: [4, 10, 9] },      // Английский
        { subjectId: 11, pairs: [4, 14] },         // Казахский язык
        { subjectId: 14, pairs: [4, 11] },         // Казахская литература
        { subjectId: 12, pairs: [4, 15] },         // Русский язык
        { subjectId: 15, pairs: [4, 12] },         // Русская литература
        { subjectId: 16, pairs: [4, 10] },         // Основы права
    ];

    // ищем пары предмета и проверяем совместимость со всеми
    const getPairs = (id) => COMPATIBLE.find((s) => s.subjectId === id)?.pairs || [];
    const areSubjectsCompatible = (a, b) => getPairs(a).includes(b) || getPairs(b).includes(a);
    const isSubjectCompatible = (subjectId, selectedSubjects) => {
        const nonHistory = selectedSubjects.filter((id) => id !== 4);
        // если уже выбраны 2 НЕ-истории → можно выбрать только историю
        if (nonHistory.length >= 2) return subjectId === 4;
        // иначе проверяем совместимость со всеми
        return nonHistory.every((id) => areSubjectsCompatible(subjectId, id));
    };


    const { data: subjects = [], isFetching: isFetchingSubjects } =
        courseSalesApi.useFetchSubjectsByCourseTypeQuery({ courseType: 'ENT' })
    const { data: coursesPrice = [], isFetching: isFetchingCoursesPrice } =
        courseSalesApi.useFetchCoursesPriceBySubjectIdsQuery({
            courseType: 'ENT',
            subjectIds: selectedSubjects
        }, {
            skip: selectedSubjects?.length === 0
        })

    const handleSubjectClick = (courseSubject) => {
        const subjectId = courseSubject?.subject?.id;
        setSelectedSubjects((prev) =>
            prev.includes(subjectId)
                ? prev.filter((id) => id !== subjectId) // обратный клик
                : isSubjectCompatible(subjectId, prev)  // проверка совместимости
                    ? [...prev, subjectId]
                    : prev
        );
    };

    useEffect(() => {
        if (coursesPrice?.length) {
            setVideoCourse(coursesPrice.find(item => item.videoCourseType === 'VIDEO_ONLY') || null);
            setMonthlyCourse(coursesPrice.find(item => item.videoCourseType === 'COURSE' && (item.monthly ?? 0) > 0) || null);
            setYearlyCourse(coursesPrice.find(item => item.videoCourseType === 'COURSE' && (item.yearly ?? 0) > 0) || null);
        }
    }, [coursesPrice])





    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'sellCourse' })}</H3>
                    </Title>
                    <ButtonGroup
                        sx={{ my: 3 }}
                        variant="outlined"
                        aria-label="outlined primary button group"
                    >
                        <Button variant="contained" >{formatMessage({ id: 'ent' })}</Button>
                    </ButtonGroup>



                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {subjects.map((item, index) => {
                            const subjectId = item?.subject?.id;  // Достаём id предмета
                            const isSelected = selectedSubjects.includes(subjectId);  // Проверяем выбран ли этот предмет (есть ли он в selectedSubjects)
                            // Предмет считается "заблокированным", если:
                            // 1. Он ещё не выбран (не isSelected)
                            // 2. И он несовместим с текущими выбранными предметами
                            const isDisabled = !isSelected && !isSubjectCompatible(subjectId, selectedSubjects);
                            return <Chip
                                color={selectedSubjects.includes(subjectId) ? 'primary' : 'default'}
                                label={item.subject.titleRu}
                                key={item.id}
                                onClick={() => handleSubjectClick(item)}
                                disabled={isDisabled}
                            />
                        })}

                    </Box>
                </Header>

                <CourseCard
                    videoCourse={videoCourse}
                    monthlyCourse={monthlyCourse}
                    yearlyCourse={yearlyCourse}
                    paymentType={paymentType}
                    setPaymentType={setPaymentType}
                    selectedSubjects={selectedSubjects}
                />

            </Content>
        </Wrapper>
    )
}

export default SellCoursePage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: ${ui.card};
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-bottom: 20px;
`
